import { h, hydrate, render } from 'preact'
import { initializeErrorReporting } from '../lib/error-reporting'
import { Provider as CustomProvider } from '../lib/context'
import styles from './components/App.css'
import { ElectionApi } from './api'
import { AppState, isError, isLoaded, ResultsState } from './store/state'
import { createStore, observeStore } from './store'
import { ReduxProvider } from '../lib/redux-provider-nohooks'
import { matchRoute } from '../lib/simple-routing/matchRoute'
import { routes } from './routes'
import { pageNotFoundRoute } from './routes/app/page-not-found'
import { getHistory } from './history'
import { IntlProvider } from '../lib/intl/IntlProvider'
import { getString } from '../lib/simple-routing/params'
import { fetchGrunndata } from './store/actions/grunndata'
import translationsPath from '../../assets/translations.json?file'
import { GA_EVENTS, initializeAnalytics } from '../lib/analytics'
import { requestThenCache, requestText } from '../lib/request'
import { isValgResultat } from './domain'
import { reloadFylkesresutater } from './store/actions/fylkesresultat'
import { reloadKommuneresultat } from './store/actions/kommuneresultat'
import { messageNoContext } from '../lib/intl/Message'
import { initializeEventLogger } from './client-event-logger'
import { version } from 'package.json'

type ClientConfig = {
  electionApiUrl: string
  widgetHost: string
}

type PreloadedState = {
  config: ClientConfig
  state: AppState
}

const history = getHistory()

function renderPage(route, store, context, messages, locale, params) {
  return (
    <ReduxProvider store={store}>
      <IntlProvider locale={locale} messages={messages} modifiers={{ widget: route.widget }}>
        <CustomProvider value={context}>{route.render(params)}</CustomProvider>
      </IntlProvider>
    </ReduxProvider>
  )
}

export class ValgResultatApp extends HTMLElement {
  async connectedCallback() {
    const el = this

    // Extract values sent from server
    const preloadedState = el.getAttribute('data-preloaded-state')
    const preloadedStateJSON: PreloadedState = preloadedState ? JSON.parse(preloadedState) : ''
    const staticHost = el && el.getAttribute('data-static-host')
    const appVersionEndpoint = el.getAttribute('data-app-version-endpoint')
    setupAutomaticClientUpdate(version, appVersionEndpoint)

    // Setup error reporting
    const { sendError } = initializeErrorReporting(el)
    const logger = {
      error: sendError
    }

    // Create google analytics tracker
    const { sendGaEvent, sendPageview } = initializeAnalytics(el)
    initializeEventLogger(el, sendGaEvent)

    // Setup redux store
    const { config, state } = preloadedStateJSON

    const store = createStore(new ElectionApi(config.electionApiUrl, '2019', true), logger, state)
    setupAutomaticUpdates(store)

    const [messages] = await Promise.all([
      requestThenCache(translationsPath).then(text => JSON.parse(text)),
      store.dispatch(fetchGrunndata())
    ])

    const pathName = history.location.pathname
    let initialMatch = matchRoute(routes, pathName)
    if (!initialMatch) {
      initialMatch = pageNotFoundRoute
      sendError(`No route found for path: ${pathName}`)
    } else if (state.page.current === 'PAGE_NOT_FOUND') {
      initialMatch = pageNotFoundRoute
    }

    const context = {
      sendGaEvent,
      sendError,
      history: initialMatch.widget ? undefined : history,
      widgetHost: config.widgetHost
    }
    const locale = getString(initialMatch.params, 'locale') || 'nb'

    // ... and render with the exact same props as on server
    el.classList.add(styles.hydrating)
    hydrate(
      renderPage(
        initialMatch,
        store,
        context,
        messages,
        locale,
        (initialMatch && initialMatch.params) || []
      ),
      el
    )
    setTimeout(() => el.classList.remove(styles.hydrating), 1000)

    // Listen for changes to the current location
    history.listen((location, action) => {
      // Maybe assign this to the initialMatch variabel so that module.hot reloads get the updated params
      let nextRoute = matchRoute(routes, location.pathname + location.search)

      if (!nextRoute) {
        nextRoute = pageNotFoundRoute
        sendError(`No route found for path: ${pathName}`)
      }
      const locale = getString(nextRoute ? nextRoute.params : undefined, 'locale') || 'nb'

      const renderRoute = route => {
        render(
          renderPage(route, store, context, messages, locale, (route && route.params) || []),
          el
        )
      }

      if (nextRoute.onRouteChanged) {
        // noinspection JSIgnoredPromiseFromCall
        nextRoute.onRouteChanged(store, nextRoute.params).catch(e => {
          if (e.status === 404) {
            renderRoute(pageNotFoundRoute)
          } else {
            sendError('Feil ved route changed: ' + e.message)
            window.location.href = 'https://static.nrk.no/error_pages/index.html'
          }
        })
      }

      let title = 'Valgresultat – Valg 2019 – NRK'
      const titleKeys = nextRoute.title && nextRoute.title(store)
      if (titleKeys) {
        title = messageNoContext(
          messages[locale],
          locale,
          sendError,
          titleKeys.id,
          titleKeys.values
        )
      }
      window.document.title = title

      renderRoute(nextRoute)

      // Setting focus on h1 to inform screen readers that the content has changed
      if (action === 'PUSH' || action === 'POP') {
        const main = document.querySelector('main')
        setTimeout(() => {
          if (main && window.innerHeight < 850) {
            main.scrollIntoView()
          } else {
            window.scrollTo(0, 0)
          }
          const header = document.querySelector('main h2') as HTMLElement
          if (header) {
            header.setAttribute('tabindex', '-1')
            header.focus()
          }
        }, 10)
      }

      sendPageview(location.pathname)
    })

    sendGaEvent(GA_EVENTS.MOUNT())

    // Enable webpack hot-reloading
    if (__HOT__ && module.hot) {
      // Reload and re-render preact application
      module.hot.accept('./routes/index.tsx', () => {
        // Hot-reloading is in a super weird state :/
        render(
          renderPage(
            initialMatch,
            store,
            context,
            messages,
            locale,
            initialMatch && initialMatch.params
          ),
          el
        )
      })
    }
  }
}

const refreshTime = 30000

function setupAutomaticUpdates(store) {
  const createStoreListener = (onExternalChange: Function) => {
    let timeout
    const loop = () => {
      onExternalChange()
      timeout = setTimeout(loop, refreshTime)
    }

    return function onStoreChange(currentState: ResultsState) {
      if (isLoaded(currentState) || isError(currentState)) {
        if ('data' in currentState && isValgResultat(currentState.data)) {
          if (currentState.data.opptalt && currentState.data.opptalt.forelopig >= 100) {
            return
          }
          timeout = setTimeout(loop, refreshTime)
        } else {
          /* Partiresultat */
          timeout = setTimeout(loop, refreshTime)
        }
      } else if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
    }
  }

  observeStore<ResultsState>(
    store,
    (state: AppState) => state.fylkesresultat,
    createStoreListener(() => store.dispatch(reloadFylkesresutater()))
  )

  observeStore<ResultsState>(
    store,
    (state: AppState) => state.kommuneresultat,
    createStoreListener(() => store.dispatch(reloadKommuneresultat()))
  )
}

const CHECK_VERSION_INTERVAL = 30 * 60 * 1000 /* 30 min */

function setupAutomaticClientUpdate(currentVersion, endpoint) {
  const checkVersion = async () => {
    const sameVersion = await requestText(endpoint)
      .then(version => version === currentVersion)
      .catch(e => true /* Uten svar antar vi at det er samme versjon */)

    if (!sameVersion) {
      location.reload()
    } else {
      setTimeout(checkVersion, CHECK_VERSION_INTERVAL)
    }
  }

  setTimeout(checkVersion, CHECK_VERSION_INTERVAL)
}

if (/* __HOT__ */ process.env.NODE_ENV === 'development') {
  // require('preact/debug')
}
